import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import './index.scss';

const AudioUploadIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const PhotoUploadIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="5" width="18" height="15" rx="3" />
    <circle cx="8" cy="11" r="2" />
    <path d="M3 17l5-4 5 4 4-3 4 3" />
  </svg>
);

const PlusIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/**
 * Compact accent pill shared by editable media surfaces and nearby create
 * actions. The caller owns the click semantics.
 */
export default function MediaUploadButton({
  variant = 'audio',
  label,
  onUpload,
  disabled = false,
  className = '',
  ariaLabel,
  tooltip = null,
  tooltipId,
}) {
  const generatedTooltipId = useId();
  const Icon = variant === 'photo'
    ? PhotoUploadIcon
    : (variant === 'audio' ? AudioUploadIcon : null);
  const hasTooltip = typeof tooltip === 'string' ? tooltip.trim().length > 0 : Boolean(tooltip);
  const resolvedTooltipId = hasTooltip
    ? (tooltipId || `media-upload-button-tooltip-${generatedTooltipId}`)
    : undefined;
  const handleClick = () => {
    if (!disabled && typeof onUpload === 'function') onUpload();
  };

  const button = (
    <button
      type="button"
      className={classNames('media-upload-button', `media-upload-button--${variant}`, className)}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      aria-describedby={resolvedTooltipId}
      title={typeof tooltip === 'string' ? tooltip : undefined}
    >
      <span className="media-upload-button__plus" aria-hidden="true">
        <PlusIcon width="14" height="14" />
      </span>
      {Icon ? <Icon width="16" height="16" aria-hidden="true" /> : null}
      <span className="media-upload-button__label">{label}</span>
    </button>
  );

  if (!hasTooltip) {
    return button;
  }

  return (
    <span
      className="media-upload-button__tooltip-wrapper"
      tabIndex={disabled ? 0 : undefined}
      aria-describedby={disabled ? resolvedTooltipId : undefined}
      title={typeof tooltip === 'string' ? tooltip : undefined}
    >
      {button}
      <span
        id={resolvedTooltipId}
        role="tooltip"
        className="media-upload-button__tooltip"
      >
        {tooltip}
      </span>
    </span>
  );
}

MediaUploadButton.propTypes = {
  variant: PropTypes.oneOf(['audio', 'photo', 'action']),
  label: PropTypes.node,
  onUpload: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  tooltip: PropTypes.node,
  tooltipId: PropTypes.string,
};
